import { Injectable, ComponentFactoryResolver, ApplicationRef, Injector, EmbeddedViewRef, ComponentRef, Type } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { OnlyEditingCurrentSessionWarningComponent } from '../../components/modals/only-editing-current-session-warning/only-editing-current-session-warning.component';

@Injectable({
    providedIn: 'root'
})
export class ModalService {
    private modalComponentRef: ComponentRef<any>;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector,
        private modalController: ModalController
    ) {}

    /**
     * Shows warning modal for editing current practice session
     * @returns Promise that resolves when modal is dismissed
     */
    public async showEditingCurrentSessionWarning(): Promise<void> {
        const modal = await this.modalController.create({
            component: OnlyEditingCurrentSessionWarningComponent,
            cssClass: 'modal-structure-5',
            backdropDismiss: true
        });

        await modal.present();

        // Wait for modal to be dismissed before resolving
        return new Promise<void>((resolve) => {
            modal.onDidDismiss().then(() => {
                resolve();
            });
        });
    }

    /**
     * Generic method to present any modal and wait for dismissal
     * @param component The component to show as modal
     * @param props Optional component props
     * @param cssClass Optional CSS class
     * @returns Promise that resolves with modal dismiss data
     */
    public async presentModal(component: any, props?: any, cssClass?: string): Promise<any> {
        const modal = await this.modalController.create({
            component,
            componentProps: props,
            cssClass: cssClass || 'modal-structure-5',
            backdropDismiss: true
        });

        await modal.present();

        return modal.onDidDismiss();
    }

    open(component: Type<any>, props?: any): Promise<any> {
        return new Promise((resolve) => {
            // 1. Create a component reference from the component
            const componentRef = this.componentFactoryResolver
                .resolveComponentFactory(component)
                .create(this.injector);

            // 2. Attach component to the appRef so that it's inside the ng component tree
            this.appRef.attachView(componentRef.hostView);

            // 3. Get DOM element from component
            const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
                .rootNodes[0] as HTMLElement;

            // 4. Append DOM element to the body
            document.body.appendChild(domElem);

            // 5. Set any input properties
            if (props) {
                Object.keys(props).forEach(key => {
                    componentRef.instance[key] = props[key];
                });
            }

            // 6. Add close/dismiss handlers
            componentRef.instance.dismiss = (data?: any) => {
                this.closeModal(componentRef);
                resolve(data);
            };

            this.modalComponentRef = componentRef;
        });
    }

    private closeModal(componentRef: ComponentRef<any>) {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
    }
}